import type * as AmplitudeNS from '@amplitude/analytics-browser';

import { USER_ID_LOCAL_STORAGE_KEY } from 'constants/session';
import { isVendorExcludedPath } from 'lib/vendorExclusion';
import config from 'utils/config';
import { DEFERRED_VENDOR_IDLE_TIMEOUT_MS, runAfterLoadAndIdle } from 'utils/performance';
import { getLocalStorageWithExpiration } from 'utils/storage';

const enabled = !!config.amplitudeApiKey;

type Amplitude = typeof AmplitudeNS;

// The Amplitude SDK (~1.3 MB) used to be pulled into the first-load bundle of
// every route via the top-level `import * as amplitude`, reached eagerly during
// root-store initialization. Init was already lazy, but the static
// import forced the code into first-load regardless. We now dynamic-`import()`
// the SDK once `startAmplitude` runs, after load + idle. Calls made before then
// are buffered and flushed in order — equivalent to the SDK's own pre-`init`
// buffering. Calls must not boot the SDK synchronously: components send events
// during first render, which would pull the SDK's parse/init work back in front
// of LCP. The first buffered call schedules the boot itself, because not every
// route reaches `loadGlobalVendors` (the `(trade)` and `(wrapped)` groups render
// without the app Providers).
let sdk: Amplitude | null = null;
let loadPromise: Promise<Amplitude | null> | null = null;
let bootRequested = false;
let bootScheduled = false;
const queue: Array<(a: Amplitude) => void> = [];
const MAX_QUEUE = 200;

// Amplitude's SDK logs delivery failures via its internal logger's `error`
// channel, which calls `console.error`. Those failures are expected — ad
// blockers, flaky networks, failed beacons — not actionable, but `console.error`
// is forwarded to Datadog and trips alerts. We route the SDK's `error` channel
// to `console.warn` so messages stay visible without paging anyone, and drop the
// pure retry-exhaustion noise entirely since it carries no actionable signal.
const SUPPRESSED_ERROR_SUBSTRINGS = ['Event rejected due to exceeded retry count'];

const loggerProvider: AmplitudeNS.Types.ILogger = {
    disable() {},
    enable() {},
    log() {
        // Intentional no-op. The SDK's `log` channel is extremely chatty,
        // verbose informational logging (event queuing, flush cycles, config
        // dumps) that gets forwarded straight to `console.log`. None of it is
        // actionable, and it drowns out everything else in the browser console,
        // so we drop it entirely. The `warn` and `error` channels are still
        // routed below, so genuine problems remain visible.
    },
    // A custom provider replaces the SDK's own log-level filter, so its debug
    // channel would print every message too (~30 ms of main thread on a throttled phone).
    debug() {},
    warn(...args) {
        console.warn(...args);
    },
    error(...args) {
        const text = args.map(String).join(' ');
        if (SUPPRESSED_ERROR_SUBSTRINGS.some((substring) => text.includes(substring))) {
            return;
        }
        console.warn(...args);
    },
};

function doInit(amplitude: Amplitude): void {
    // Deterministically enable autocapture for ~10% of devices using Amplitude's
    // persisted device ID so the same user stays consistently in or out across sessions.
    // First visit (no stored device ID yet) falls back to Math.random() so new users
    // still have a chance to be sampled; subsequent sessions use the stable hash.
    let enableAutocapture = Math.random() < 0.1;
    try {
        const stored = localStorage.getItem(`AMP_${config.amplitudeApiKey.slice(0, 10)}`);
        if (stored) {
            const deviceId: string = JSON.parse(stored).deviceId ?? '';
            let hash = 0;
            for (let i = 0; i < deviceId.length; i++) {
                hash = (hash * 31 + deviceId.charCodeAt(i)) | 0;
            }
            enableAutocapture = Math.abs(hash) % 10 === 0;
        }
    } catch {
        // Corrupt storage — fall through with Math.random() default.
    }
    amplitude.init(config.amplitudeApiKey, undefined, {
        autocapture: {
            elementInteractions: enableAutocapture,
            frustrationInteractions: true,
        },
        transport: 'beacon',
        loggerProvider,
    });
}

function load(): void {
    if (loadPromise || !enabled || typeof window === 'undefined') return;

    loadPromise = import('@amplitude/analytics-browser')
        .then((amplitude) => {
            doInit(amplitude);
            sdk = amplitude;
            for (const op of queue.splice(0)) op(amplitude);
            return amplitude;
        })
        .catch((err: unknown) => {
            queue.length = 0;
            console.warn('Amplitude failed to load', err);
            return null;
        });
}

/** Run `op` against the SDK, or buffer it and boot the SDK after load + idle. */
function withSdk(op: (a: Amplitude) => void): void {
    if (!enabled || typeof window === 'undefined') return;
    if (sdk) {
        op(sdk);
        return;
    }
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push(op);
    if (bootRequested) {
        load();
    } else if (!bootScheduled) {
        bootScheduled = true;
        runAfterLoadAndIdle(bootUnlessExcluded, { timeout: DEFERRED_VENDOR_IDLE_TIMEOUT_MS });
    }
}

function bootUnlessExcluded(): void {
    // The visitor may have soft-navigated to an excluded route while this waited
    // for idle. Keep the buffer and let the next call reschedule the boot.
    if (isVendorExcludedPath()) {
        bootScheduled = false;
        return;
    }
    startAmplitude();
}

export function init(userId?: string): void {
    withSdk((amplitude) => {
        if (userId) {
            amplitude.setUserId(userId);
        }
    });
}

/**
 * Boot Amplitude (after load + idle, from `loadGlobalVendors` or the first
 * buffered call, whichever schedules it first) and flush every
 * call buffered until now. Seeds the user id from localStorage, ahead of that
 * buffer, so the SDK attributes early events to the returning user.
 * Idempotent via the module's load-promise guard.
 */
export function startAmplitude(): void {
    const userId = typeof window !== 'undefined' ? getLocalStorageWithExpiration(USER_ID_LOCAL_STORAGE_KEY) : null;
    if (userId && enabled && !sdk) {
        queue.unshift((amplitude) => amplitude.setUserId(userId));
    }
    bootRequested = true;
    load();
}

function buildIdentifyEvent(amplitude: Amplitude, properties: Record<string, unknown>): AmplitudeNS.Identify {
    const identifyEvent = new amplitude.Identify();
    for (const [key, value] of Object.entries(properties)) {
        if (value != null) {
            identifyEvent.set(key, value as AmplitudeNS.Types.ValidPropertyType);
        }
    }
    return identifyEvent;
}

export function setUserProperties(properties: Record<string, unknown>): void {
    withSdk((amplitude) => {
        amplitude.identify(buildIdentifyEvent(amplitude, properties));
    });
}

export function identify(userId: string, properties?: Record<string, unknown>): void {
    withSdk((amplitude) => {
        amplitude.setUserId(userId);

        if (properties) {
            amplitude.identify(buildIdentifyEvent(amplitude, properties));
        }
    });
}

export function reset(): void {
    withSdk((amplitude) => {
        amplitude.reset();
    });
}

const EVENT_NAME_OVERRIDES: Record<string, string> = {
    page_load: 'Page Viewed',
};

/**
 * Converts internal snake_case event names to Amplitude Title Case.
 * e.g. "signup_step" -> "Signup Step", "page_load" -> "Page Viewed"
 */
function toAmplitudeEventName(event: string): string {
    return (
        EVENT_NAME_OVERRIDES[event] ??
        event
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    );
}

export function sendEvent(event: string, content?: Record<string, any>): void {
    // Stamp the event now: it may sit in the boot buffer for seconds before the SDK sees it.
    const time = Date.now();
    withSdk((amplitude) => {
        amplitude.track(toAmplitudeEventName(event), content, { time });
    });
}
